import { put, call, takeLatest } from 'redux-saga/effects';
import * as R from 'ramda';
import { requestWithAuth, request, processErr } from '../api/request';
import * as actions from '../actions/user';
import {
    GET_HOTUSER_LIST_REQUEST,
    ADD_HOTUSER_REQUEST,
    DELETE_HOTUSER_REQUEST,
    SYNC_HOTUSER_REQUEST,
    GET_USER_LIST_REQUEST,
    LOGIN_REQUEST,
    SIGNUP_REQUEST,
    RESEND_REQUEST,
    RESET_PASSWORD_REQUEST,
} from '../constant/actionTypes';

const url = '/user';
const pageSize = 20;

const resetPasswordAPI = payload => {
    return request().put(`${url}/password`, payload)
        .then(res => {
            if (res.status === 200) {
                return {
                    ...res.data,
                    status: 'success'
                };
            }
        }).catch(err => { throw processErr(err); })
};

const resendAPI = payload => {
    return request().post(`${url}/resend`, payload)
        .then(res => {
            if (res.status === 200) {
                return {
                    ...res.data,
                    status: 'success'
                };
            }
        }).catch(err => { throw processErr(err); })
};

const signupAPI = payload => {
    return request().post(`${url}/signup`, payload)
        .then(res => {
            if (res.status === 200) {
                return {
                    ...res.data,
                    status: 'success'
                };
            }
        }).catch(err => { throw processErr(err); })
};

const loginAPI = payload => {
    return request().post(`${url}/signin`, payload)
        .then(res => {
            if (res.status === 200) {
                return {
                    ...res.data,
                    status: 'success'
                };
            }
        }).catch(err => { throw processErr(err); })
};

const getHotUserListAPI = payload => {
    const {
        page
    } = payload
    let endpoint = `${url}/hots?size=${pageSize}`;
    if (!R.isNil(page)) {
        endpoint += `&page=${page - 1}`;
    }
    return requestWithAuth().get(endpoint)
        .then(res => ({
            data: res.data.list,
            pagination: {
                page: R.isNil(page) ? undefined : page,
                pages: Math.ceil(res.data.size / pageSize),
                total: res.data.size,
            }
        })).catch(err => { throw processErr(err); })
}

const addHotUserAPI = payload => {
    return requestWithAuth().post(`${url}/hots`, payload)
        .then(res => ({
            ...res.data,
            status: 'success'
        })).catch(err => { throw processErr(err); })
}

const deleteHotUserAPI = payload => {
    return requestWithAuth().delete(`${url}/hots?uid=${payload.uid}`)
        .then(res => ({
            ...res.data,
            status: 'success'
        })).catch(err => { throw processErr(err); })
}

const syncHotUserAPI = payload => {
    return requestWithAuth().post(`${url}/hots/sync`, payload)
        .then(res => ({
            ...res.data,
            status: 'success'
        })).catch(err => { throw processErr(err); })
}

const getUserListAPI = payload => {
    const {
        page,
        uid,
        email,
        phone,
        name,
        nickname,
    } = payload
    let endpoint = `${url}/search?size=${pageSize}`;
    if (!R.isNil(page)) {
        endpoint += `&page=${page - 1}`;
    }
    if (!R.isNil(uid)) {
        endpoint += `&uid=${encodeURIComponent(uid)}`;
    }
    if (!R.isNil(email)) {
        endpoint += `&email=${encodeURIComponent(email)}`;
    }
    if (!R.isNil(phone)) {
        endpoint += `&phone=${encodeURIComponent(phone)}`;
    }
    if (!R.isNil(name)) {
        endpoint += `&name=${encodeURIComponent(name)}`;
    }
    if (!R.isNil(nickname)) {
        endpoint += `&nickname=${encodeURIComponent(nickname)}`;
    }
    return requestWithAuth().get(endpoint)
        .then(res => ({
            data: res.data.list,
            pagination: {
                page: R.isNil(page) ? undefined : page,
                pages: Math.ceil(res.data.size / pageSize),
                total: res.data.size,
            }
        })).catch(err => { throw processErr(err); })
}

function* getHotUserList(action) {
    try {
        const list = yield call(getHotUserListAPI, action.payload);
        yield put(actions.getHotUserListSuccess(list));
    } catch (e) {
        yield put(actions.getHotUserListFailure(e));
    }
}

function* addHotUser(action) {
    try {
        const hot = yield call(addHotUserAPI, action.payload);
        yield put(actions.addHotUserSuccess(hot));
        yield getHotUserList({ payload: { page: 1 } });
    } catch (e) {
        yield put(actions.addHotUserFailure(e));
    }
}

function* deleteHotUser(action) {
    try {
        const result = yield call(deleteHotUserAPI, action.payload);
        yield put(actions.deleteHotUserSuccess(result));
        yield getHotUserList({ payload: { page: 1 } });
    } catch (e) {
        yield put(actions.deleteHotUserFailure(e));
    }
}

function* syncHotUser(action) {
    try {
        const result = yield call(syncHotUserAPI, action.payload);
        yield put(actions.syncHotUserSuccess(result));
        yield getHotUserList({ payload: { page: 1 } });
    } catch (e) {
        yield put(actions.syncHotUserFailure(e));
    }
}

function* getUserList(action) {
    try {
        const list = yield call(getUserListAPI, action.payload);
        yield put(actions.getUserListSuccess(list));
    } catch (e) {
        yield put(actions.getUserListFailure(e));
    }
}

function* login(action) {
    try {
        const user = yield call(loginAPI, action.payload);
        yield put(actions.loginSuccess(user));
    } catch (e) {
        yield put(actions.loginFailure(e));
    }
}

function* signup(action) {
    try {
        const user = yield call(signupAPI, action.payload);
        yield put(actions.signupSuccess(user));
    } catch (e) {
        yield put(actions.signupFailure(e));
    }
}

function* resend(action) {
    try {
        const user = yield call(resendAPI, action.payload);
        yield put(actions.resendSuccess(user));
    } catch (e) {
        yield put(actions.resendFailure(e));
    }
}

function* resetPassword(action) {
    try {
        const user = yield call(resetPasswordAPI, action.payload);
        yield put(actions.resetPasswordSuccess(user));
    } catch (e) {
        yield put(actions.resetPasswordFailure(e));
    }
}

const user = [
    takeLatest(GET_HOTUSER_LIST_REQUEST, getHotUserList),
    takeLatest(ADD_HOTUSER_REQUEST, addHotUser),
    takeLatest(DELETE_HOTUSER_REQUEST, deleteHotUser),
    takeLatest(SYNC_HOTUSER_REQUEST, syncHotUser),
    takeLatest(GET_USER_LIST_REQUEST, getUserList),
    takeLatest(LOGIN_REQUEST, login),
    takeLatest(SIGNUP_REQUEST, signup),
    takeLatest(RESEND_REQUEST, resend),
    takeLatest(RESET_PASSWORD_REQUEST, resetPassword),
];

export default user;